<template>
    <global-modal v-model="isOpened" width="520px">
        <div class="add-to-project">
            <h4 class="add-to-project-headline">{{ config.texts.headline }}</h4>

            <template v-if="!isSignedIn">
                <p class="add-to-project-note">{{ config.texts.signedOut }}</p>
                <button class="btn btn-primary small" type="button" @click="signIn">
                    {{ config.texts.signIn }}
                </button>
            </template>

            <template v-else-if="isDone">
                <p class="add-to-project-note">{{ config.texts.done }}</p>
                <a v-if="detailsUrl" class="btn btn-primary small" :href="detailsUrl">
                    {{ config.texts.openProject }}
                </a>
            </template>

            <template v-else>
                <div class="form-group">
                    <label for="add-to-project-select">{{ config.texts.label }}</label>
                    <select id="add-to-project-select" class="form-control" v-model="projectId">
                        <option v-for="project in projects" :key="project.id" :value="project.id">
                            {{ project.name }}
                        </option>
                        <option :value="NEW_PROJECT">{{ config.texts.newOption }}</option>
                    </select>
                </div>
                <div v-if="isCreating" class="form-group">
                    <input class="form-control" type="text" maxlength="190"
                           :placeholder="config.texts.newPlaceholder"
                           v-model="projectName">
                </div>
                <button class="btn btn-primary small" type="button" :disabled="isSaving || !canSubmit" @click="submit">
                    {{ config.texts.submit }}
                </button>
            </template>
        </div>
    </global-modal>
</template>

<script setup lang="ts">
import {GlobalModal} from '@webstacz/shop-components';
import {computed, reactive, ref} from 'vue';
import axios from '@/plugins/axios';

type Project = { id: number; name: string };

export type AddToProjectConfig = {
    optionsUrl: string;
    attachUrl: string;
    token: string;
    texts: {
        headline: string;
        label: string;
        newOption: string;
        newPlaceholder: string;
        submit: string;
        signedOut: string;
        signIn: string;
        done: string;
        openProject: string;
    };
};

const NEW_PROJECT = '__new';

const config = reactive<AddToProjectConfig>({
    optionsUrl: '',
    attachUrl: '',
    token: '',
    texts: {
        headline: '', label: '', newOption: '', newPlaceholder: '',
        submit: '', signedOut: '', signIn: '', done: '', openProject: '',
    },
});

const isOpened = ref(false);
const isSignedIn = ref(false);
const isSaving = ref(false);
const isDone = ref(false);
const projects = ref<Project[]>([]);
const projectId = ref<number | typeof NEW_PROJECT | ''>('');
const projectName = ref('');
const detailsUrl = ref('');

const isCreating = computed(() => projectId.value === NEW_PROJECT);

const canSubmit = computed(() => isCreating.value
    ? projectName.value.trim() !== ''
    : projectId.value !== '');

/**
 * The product page comes from the shared full page cache, so the button is in
 * the HTML for everyone and the signed in state is only known here.
 *
 * @returns {Promise<void>}
 */
const loadProjects = async (): Promise<void> => {
    try {
        const {data} = await axios.get(config.optionsUrl);
        projects.value = data?.projects ?? [];
        // with no project yet the only sensible choice is to create one
        projectId.value = projects.value.length > 0 ? projects.value[0].id : NEW_PROJECT;
    } catch (e) {
        projects.value = [];
        projectId.value = NEW_PROJECT;
    }
};

const signIn = (): void => {
    isOpened.value = false;
    window.openLoginModal?.({view: 'login'});
};

/**
 * @returns {Promise<void>}
 */
const submit = async (): Promise<void> => {
    isSaving.value = true;

    try {
        const {data} = await axios.post(config.attachUrl, {
            _token: config.token,
            project_id: isCreating.value ? null : projectId.value,
            project_name: isCreating.value ? projectName.value.trim() : null,
        });
        detailsUrl.value = data?.detailsUrl ?? '';
        isDone.value = true;
    } finally {
        isSaving.value = false;
    }
};

window.openAddToProjectModal = (modalConfig: AddToProjectConfig): void => {
    Object.assign(config, modalConfig);
    isDone.value = false;
    projectName.value = '';
    detailsUrl.value = '';
    isSignedIn.value = window.customerAuth?.isAuthenticated === true;
    isOpened.value = true;

    if (isSignedIn.value) {
        loadProjects();
    }
};
</script>

<style scoped lang="scss">
.add-to-project-headline {
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    // keeps a long headline clear of the modal's close button
    margin: 0 24px 16px 0;
}

.add-to-project-note {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 22px;
}

.add-to-project {
    .btn {
        border: 0;

        &:disabled {
            opacity: .45;
            cursor: not-allowed;
        }
    }
}
</style>
